export const CLINICAL_UPLOAD_DOC_TYPES: Array<{ value: string; label: string }> = [
  { value: 'POC', label: 'Speech Plan of Care (POC)' },
  { value: 'FEEDING_POC', label: 'Feeding Plan of Care (POC)' },
  { value: 'PHYSICIAN_SIGNED_POC', label: 'Speech CPOC' },
  { value: 'PHYSICIAN_SIGNED_FEEDING_POC', label: 'Feeding CPOC' },
  { value: 'EVAL', label: 'Speech Evaluation' },
  { value: 'FEEDING_EVAL', label: 'Feeding Evaluation' },
  { value: 'QPR', label: 'Quarterly Progress Report (QPR)' },
  { value: 'EXTERNAL', label: 'External Clinical Document' },
  { value: 'OTHER', label: 'Other Clinical Document' }
];

export function isPhysicianSignedCpoc(doc: {
  doc_type?: string | null;
  title?: string | null;
  physician_signature?: string | null;
}): boolean {
  const dt = String(doc.doc_type || '').toUpperCase();
  const title = String(doc.title || '').toLowerCase();
  if (dt === 'PHYSICIAN_SIGNED_POC' || dt === 'PHYSICIAN_SIGNED_FEEDING_POC') return true;
  if (title.includes('physician-signed cpoc') || title.includes('physician signed cpoc')) return true;
  if (title.includes('physician-signed') && (title.includes('poc') || title.includes('cpoc'))) return true;
  return dt === 'POC' && Boolean(doc.physician_signature);
}

export function isFeedingClinicalDoc(doc: { doc_type?: string | null; title?: string | null }): boolean {
  const dt = String(doc.doc_type || '').toUpperCase();
  if (dt === 'FEEDING_POC' || dt === 'FEEDING_EVAL' || dt === 'FEEDING_EVALUATION' || dt === 'PHYSICIAN_SIGNED_FEEDING_POC') return true;
  const title = String(doc.title || '').toLowerCase();
  return title.includes('feeding');
}

export function isSpeechOrFeedingPoc(docType?: string | null): boolean {
  const dt = String(docType || '').toUpperCase();
  return dt === 'POC' || dt === 'FEEDING_POC' || dt === 'PHYSICIAN_SIGNED_POC';
}

/** Authored plan recertification. Uploaded physician-signed plans are not included. */
export function isAuthoredPlanDoc(docType?: string | null): boolean {
  const dt = String(docType || '').toUpperCase();
  return dt === 'POC' || dt === 'FEEDING_POC';
}

/** Authored evaluation, including a feeding evaluation. */
export function isAuthoredEvalDoc(docType?: string | null): boolean {
  const dt = String(docType || '').toUpperCase();
  return dt === 'EVAL' || dt === 'FEEDING_EVAL' || dt === 'FEEDING_EVALUATION';
}

export function isFeedingPlanDoc(docType?: string | null): boolean {
  return String(docType || '').toUpperCase() === 'FEEDING_POC';
}

export function isFeedingEvalDoc(docType?: string | null): boolean {
  const dt = String(docType || '').toUpperCase();
  return dt === 'FEEDING_EVAL' || dt === 'FEEDING_EVALUATION';
}

export const SPEECH_EVALUATION_CPTS = ['92523', '92521', '92522', '92524'] as const;
export const FEEDING_EVALUATION_CPT = '92610';
export const SPEECH_TREATMENT_CPT = '92507';
export const FEEDING_TREATMENT_CPT = '92526';

const SOAP_DATE = '\\d{4}-\\d{2}-\\d{2}';
const GENERIC_SPEECH_SOAP_TITLES = [
  new RegExp(`^Daily Speech Treatment Note - ${SOAP_DATE}$`),
  new RegExp(`^Daily Speech Treatment Note - .+ \\(${SOAP_DATE}\\)$`),
  /^Daily Speech Therapy Treatment Note$/,
  new RegExp(`^Daily Speech Therapy Treatment Note - ${SOAP_DATE}$`)
];
const GENERIC_FEEDING_SOAP_TITLES = [
  new RegExp(`^Daily Feeding Treatment Note - ${SOAP_DATE}$`),
  new RegExp(`^Daily Feeding Treatment Note - .+ \\(${SOAP_DATE}\\)$`),
  /^Daily Feeding Therapy Treatment Note$/,
  new RegExp(`^Daily Feeding Therapy Treatment Note - ${SOAP_DATE}$`)
];

export function isGenericSpeechSoapTitle(title?: string | null): boolean {
  const value = String(title || '').trim();
  return GENERIC_SPEECH_SOAP_TITLES.some((pattern) => pattern.test(value));
}

export function isUntouchedSoapTitle(title?: string | null): boolean {
  const value = String(title || '').trim();
  if (!value) return true;
  return isGenericSpeechSoapTitle(value) || GENERIC_FEEDING_SOAP_TITLES.some((pattern) => pattern.test(value));
}

export function defaultSoapTitle(input: {
  serviceCode?: string | null;
  clientName?: string | null;
  dos?: string | null;
  style: 'mobile' | 'modal' | 'service';
}): string {
  const feeding = String(input.serviceCode || '').trim() === FEEDING_TREATMENT_CPT;
  const dos = String(input.dos || '');
  if (input.style === 'service') {
    return feeding
      ? `Daily Feeding Therapy Treatment Note - ${dos}`
      : `Daily Speech Therapy Treatment Note - ${dos}`;
  }
  if (input.style === 'modal') {
    const clientName = String(input.clientName || 'Member');
    return feeding
      ? `Daily Feeding Treatment Note - ${clientName} (${dos})`
      : `Daily Speech Treatment Note - ${clientName} (${dos})`;
  }
  return feeding
    ? `Daily Feeding Treatment Note - ${dos}`
    : `Daily Speech Treatment Note - ${dos}`;
}

export function soapTitleAfterDefaultRefresh(currentTitle: string, nextDefault: string): string {
  return isUntouchedSoapTitle(currentTitle) ? nextDefault : currentTitle;
}

export function soapDisplayTitle(storedTitle?: string | null, serviceCode?: string | null): string {
  const title = String(storedTitle || '').trim();
  if (String(serviceCode || '').trim() !== FEEDING_TREATMENT_CPT || !isGenericSpeechSoapTitle(title)) return String(storedTitle || '');
  if (title.startsWith('Daily Speech Therapy Treatment Note')) {
    return title.replace('Daily Speech Therapy Treatment Note', 'Daily Feeding Therapy Treatment Note');
  }
  return title.replace('Daily Speech Treatment Note', 'Daily Feeding Treatment Note');
}

export function soapServiceCode(doc?: { service_code?: string | null; section_data_json?: unknown } | null): string {
  const direct = String(doc?.service_code || '').trim();
  if (direct) return direct;
  const section = doc?.section_data_json;
  if (!section) return '';
  try {
    const parsed = typeof section === 'string' ? JSON.parse(section) : section;
    return String((parsed as { service_code?: string | null })?.service_code || '').trim();
  } catch {
    return '';
  }
}

/** Feeding SOAP viewer title. Null leaves the existing Speech viewer label in place. */
export function feedingSoapViewerTitle(storedTitle: string, doc?: { service_code?: string | null; section_data_json?: unknown } | null): string | null {
  if (soapServiceCode(doc) !== FEEDING_TREATMENT_CPT) return null;
  const stored = String(storedTitle || '').trim();
  if (!stored) return null;
  return soapDisplayTitle(stored, FEEDING_TREATMENT_CPT);
}

export function authoredClinicalDocType(
  modalDocType: string,
  discipline?: string | null
): string {
  const feeding = String(discipline || '').trim().toUpperCase() === 'FEEDING';
  if (modalDocType === 'POC') return feeding ? 'FEEDING_POC' : 'POC';
  if (modalDocType === 'EVAL') return feeding ? 'FEEDING_EVAL' : 'EVAL';
  return modalDocType;
}

export const AUTHORED_EVAL_TREATMENT_SETTINGS = ['Home / Community', 'Teletherapy'] as const;
export type AuthoredEvalTreatmentSetting = (typeof AUTHORED_EVAL_TREATMENT_SETTINGS)[number];

/** New authored evaluations must choose one of these two stored values. Historical values are not rewritten. */
export function parseAuthoredEvalTreatmentSetting(
  raw: unknown
): { ok: true; value: AuthoredEvalTreatmentSetting } | { ok: false } {
  const value = String(raw ?? '').trim();
  if (value === 'Home / Community' || value === 'Teletherapy') {
    return { ok: true, value };
  }
  return { ok: false };
}

export function evaluationAuthoringSaveFields(input: {
  discipline?: string | null;
  medical_necessity_rationale?: string | null;
  speechDefault: string;
}): {
  doc_type: string;
  medical_necessity: string;
  medical_necessity_rationale: string;
} {
  return {
    doc_type: authoredClinicalDocType('EVAL', input.discipline),
    medical_necessity: planMedicalNecessityForSave({
      discipline: input.discipline,
      statement: input.medical_necessity_rationale,
      speechDefault: input.speechDefault
    }),
    medical_necessity_rationale: String(input.medical_necessity_rationale || '')
  };
}

export function planMedicalNecessityForSave(input: {
  discipline?: string | null;
  statement?: string | null;
  speechDefault: string;
}): string {
  if (String(input.discipline || '').trim().toUpperCase() === 'FEEDING') return input.statement || '';
  return input.statement || input.speechDefault;
}

export function continuedSkilledNeedDisplay(docType?: string | null, value?: string | null): string {
  const text = String(value || '').trim();
  if (text) return String(value);
  if (isFeedingPlanDoc(docType)) return 'Not recorded';
  return 'Skilled clinical intervention is required to progress functional communication.';
}

export function feedingSkilledNeedOutput(docType?: string | null, value?: string | null): string {
  const text = String(value || '').trim();
  if (text) return String(value);
  return isFeedingPlanDoc(docType) ? 'Not recorded' : '';
}

export function goalTermLabel(goal?: { goal_term?: string | null } | null): string {
  const term = String(goal?.goal_term || '').trim().toUpperCase();
  if (term === 'LONG' || term === 'LONG_TERM') return 'Long-term';
  if (term === 'SHORT' || term === 'SHORT_TERM') return 'Short-term';
  return '';
}

function isoDateOnly(value?: string | null): { y: number; m: number; d: number } | null {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!y || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

export function rejectsGuardianSignWell(docType?: string | null): boolean {
  const dt = String(docType || '').toUpperCase();
  return dt === 'POC' || dt === 'FEEDING_POC' || dt === 'QPR' || dt === 'PHYSICIAN_SIGNED_POC';
}

/** Newly authored evaluations persist the evaluation_date field, including when it is cleared. */
export function hasStoredEvaluationDateField(section?: object | null): boolean {
  return !!section && typeof section === 'object' && Object.prototype.hasOwnProperty.call(section, 'evaluation_date');
}

export function parseClinicalSection(value: unknown): Record<string, any> | null {
  if (!value) return null;
  if (typeof value === 'object') return value as Record<string, any>;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function isRealCalendarDate(value: string): boolean {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}

/**
 * Encounter clock used only when a new evaluation service is inserted.
 * A complete clock is stored as entered.
 * A newly authored evaluation stores the evaluation_date field. A missing or unusable clock returns null.
 * A legacy document without that field keeps the prior 09:00-10:00 service default.
 * An existing service row is not updated.
 */
export function evaluationEncounterTimes(section?: { time_in?: string | null; time_out?: string | null; evaluation_date?: string | null } | null): { start: string; end: string; duration: number } | null {
  const clock = (value?: string | null) => {
    const text = String(value || '');
    const match = text.match(/^(\d{2}):(\d{2})$/);
    if (!match) return '';
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return '';
    return text;
  };
  const start = clock(section?.time_in);
  const end = clock(section?.time_out);
  if (start && end) {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const minutes = (eh * 60 + em) - (sh * 60 + sm);
    if (minutes > 0 && minutes < 24 * 60) return { start, end, duration: minutes };
  }
  if (hasStoredEvaluationDateField(section)) return null;
  return { start: '09:00', end: '10:00', duration: 60 };
}

export function newlyAuthoredEvaluationEncounterIssue(section?: object | null): { code: 'EVALUATION_ENCOUNTER_REQUIRED'; error: string } | null {
  if (!hasStoredEvaluationDateField(section)) return null;
  const date = String((section as { evaluation_date?: string | null }).evaluation_date ?? '').trim();
  const dateOk = isRealCalendarDate(date);
  const times = evaluationEncounterTimes(section as { time_in?: string | null; time_out?: string | null; evaluation_date?: string | null });
  if (dateOk && times) return null;
  const need = !dateOk && !times
    ? 'the evaluation date and valid start and stop times'
    : !dateOk
      ? 'the evaluation date'
      : 'valid start and stop times';
  return {
    code: 'EVALUATION_ENCOUNTER_REQUIRED',
    error: `Enter ${need} before this evaluation can be finalized. The document stays editable.`
  };
}

export type EvaluationSignPath = 'clinician-sign' | 'guardian-sign' | 'remote-guardian';

/** In-app signing waits for a complete encounter. Remote guardian evidence is stored and finalization waits. */
export function evaluationCompletionDecision(section: object | null | undefined, path: EvaluationSignPath): {
  allowSignatureWrite: boolean;
  allowFinalize: boolean;
  issue: { code: 'EVALUATION_ENCOUNTER_REQUIRED'; error: string } | null;
} {
  const issue = newlyAuthoredEvaluationEncounterIssue(section);
  if (!issue) return { allowSignatureWrite: true, allowFinalize: true, issue: null };
  if (path === 'remote-guardian') return { allowSignatureWrite: true, allowFinalize: false, issue };
  return { allowSignatureWrite: false, allowFinalize: false, issue };
}

export interface EvaluationServicePlan {
  serviceId: string;
  dos: string;
  serviceCode: string;
  placeOfService: string;
  modifier: string | null;
  startTime: string | null;
  endTime: string | null;
  durationMinutes: number | null;
  reason: 'READY' | 'ENCOUNTER_TIMES_NOT_RECORDED';
}

/** Fields a new evaluation service would use. This does not read or write stored services. */
export function plannedEvaluationServiceFields(
  doc: { id: string; doc_type?: string | null; title?: string | null; poc_start_date?: string | null; document_date?: string | null; created_at?: string | null },
  sectionData?: { eval_cpt_code?: string | null; treatment_setting?: string | null; time_in?: string | null; time_out?: string | null; evaluation_date?: string | null } | null
): EvaluationServicePlan {
  let dos = evaluationServiceDate(sectionData, doc);
  if (!dos && doc.created_at) dos = String(doc.created_at).substring(0, 10);
  if (!dos) dos = new Date().toISOString().substring(0, 10);

  const feedingEvaluation = isFeedingEvalDoc(doc.doc_type);
  let serviceCode = feedingEvaluation ? FEEDING_EVALUATION_CPT : '92523';
  const requestedCode = sectionData?.eval_cpt_code;
  if (!feedingEvaluation && requestedCode && ['92523', '92521', '92522', '92524', '92610'].includes(requestedCode)) {
    serviceCode = requestedCode;
  }

  let placeOfService = '12';
  let modifier = '';
  const setting = String(sectionData?.treatment_setting || '').toLowerCase();
  const docTitle = String(doc.title || '').toLowerCase();
  if (setting.includes('tele') || setting.includes('virtual') || docTitle.includes('tele')) {
    placeOfService = '02';
    modifier = '95';
  } else if (setting.includes('office') || setting.includes('clinic')) {
    placeOfService = '11';
  } else if (setting.includes('school')) {
    placeOfService = '03';
  }

  const encounterTimes = evaluationEncounterTimes(sectionData);
  return {
    serviceId: `SRV-EVAL-${doc.id}`,
    dos,
    serviceCode,
    placeOfService,
    modifier: modifier || null,
    startTime: encounterTimes?.start ?? null,
    endTime: encounterTimes?.end ?? null,
    durationMinutes: encounterTimes?.duration ?? null,
    reason: encounterTimes ? 'READY' : 'ENCOUNTER_TIMES_NOT_RECORDED'
  };
}

export interface CanonicalGoalInsert {
  id: string;
  docId: string;
  originDocId: string;
  category: string;
  description: string;
  baseline: string;
  targetPercentage: number;
  currentPercentage: number;
  targetDate: string | null;
  status: 'IN_PROGRESS';
}

/**
 * The canonical insert for a new evaluation goal or a plan goal explicitly marked ADD.
 * An existing plan goal without ADD is not inserted again. Goal-term labels stay on the document JSON.
 */
export function canonicalGoalInsert(
  docType: string,
  docId: string,
  goal: any,
  alreadyStored: boolean,
  nextId: () => string = () => 'GOL-' + Math.floor(1000 + Math.random() * 9000)
): CanonicalGoalInsert | null {
  if (!goal || alreadyStored) return null;
  const description = String(goal.description || goal.goal_text || goal.title || '').trim();
  if (!description) return null;

  const isEval = isAuthoredEvalDoc(docType);
  const isPlan = isAuthoredPlanDoc(docType);
  const action = String(goal.action || 'CONTINUE').toUpperCase();
  const wantsNewPlanGoal = action === 'ADD' || !goal.id || String(goal.id).startsWith('GOL-NEW-');
  if (!isEval && !(isPlan && wantsNewPlanGoal)) return null;

  const storedId = String(goal.id || '');
  const id = isEval && storedId.startsWith('GOL-') && !storedId.startsWith('GOL-INIT-') && !storedId.startsWith('GOL-NEW-')
    ? storedId
    : nextId();
  const targetPercentage = typeof goal.target_percentage === 'number'
    ? goal.target_percentage
    : (parseInt(goal.target || '80', 10) || 80);

  return {
    id,
    docId,
    originDocId: docId,
    category: goal.category || 'Speech & Language',
    description,
    baseline: goal.baseline || goal.baseline_performance || 'Established in Plan of Care',
    targetPercentage,
    currentPercentage: typeof goal.current_percentage === 'number' ? goal.current_percentage : 0,
    targetDate: goal.target_date || goal.targetDate || null,
    status: 'IN_PROGRESS'
  };
}

/** A stored evaluation date is the service date. Older documents keep the plan-start fallback. */
export function evaluationServiceDate(section?: { evaluation_date?: string | null } | null, fallback?: { poc_start_date?: string | null; document_date?: string | null } | null): string {
  return String(section?.evaluation_date || fallback?.poc_start_date || fallback?.document_date || '');
}

/** Age in completed years on the evaluation date. Returns null when either date is missing. */
export function ageAtEvaluation(dob?: string | null, evaluationDate?: string | null): number | null {
  const birth = isoDateOnly(dob);
  const on = isoDateOnly(evaluationDate);
  if (!birth || !on) return null;
  let age = on.y - birth.y;
  if (on.m < birth.m || (on.m === birth.m && on.d < birth.d)) age -= 1;
  if (age < 0 || age > 130) return null;
  return age;
}

export function formatClinicalDocTypeLabel(docType?: string | null, title?: string | null): string {
  const dt = String(docType || '').toUpperCase();
  const t = String(title || '').toLowerCase();
  if (dt === 'PHYSICIAN_SIGNED_FEEDING_POC') {
    return 'Feeding CPOC';
  }
  if (dt === 'PHYSICIAN_SIGNED_POC' || t.includes('physician-signed cpoc') || t.includes('physician signed cpoc')) {
    return 'Speech CPOC';
  }
  if (dt === 'FEEDING_POC' || (t.includes('feeding') && t.includes('plan of care'))) {
    return 'Feeding Plan of Care (POC)';
  }
  if (dt === 'FEEDING_EVAL' || dt === 'FEEDING_EVALUATION' || t.includes('feeding evaluation')) {
    return 'Feeding Evaluation';
  }
  if (dt === 'POC') return 'Speech Plan of Care (POC)';
  if (dt === 'EVAL' || dt === 'EVALUATION') return 'Speech Evaluation';
  if (dt === 'SOAP') return 'SOAP Note';
  if (dt === 'QPR') return 'Quarterly Progress Report (QPR)';
  if (dt === 'INTAKE_PACKET' || dt === 'INTAKE') return 'Intake Packet';
  if (dt === 'DDD_PACKET') return 'DDD Pre-Service Orientation';
  if (dt === 'EXTERNAL') return 'External Document';
  if (dt === 'OTHER') return 'Clinical Document';
  if (!dt) return 'Clinical Document';
  return dt.replace(/_/g, ' ');
}

export function defaultClinicalUploadTitle(docType: string): string {
  switch (String(docType || '').toUpperCase()) {
    case 'PHYSICIAN_SIGNED_POC':
      return 'Speech CPOC';
    case 'PHYSICIAN_SIGNED_FEEDING_POC':
      return 'Feeding CPOC';
    case 'POC':
      return 'Speech Plan of Care';
    case 'FEEDING_POC':
      return 'Feeding Plan of Care';
    case 'EVAL':
      return 'Speech Evaluation';
    case 'FEEDING_EVAL':
    case 'FEEDING_EVALUATION':
      return 'Feeding Evaluation';
    case 'QPR':
      return 'Quarterly Progress Report';
    case 'EXTERNAL':
      return 'External Clinical Document';
    default:
      return 'Client Clinical Document';
  }
}

export function derivePocActiveExpired(endDate?: string | null): 'Active' | 'Expired' | null {
  const raw = String(endDate || '').trim();
  if (!raw || raw.includes(';')) return null;
  const end = new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T00:00:00` : raw);
  if (isNaN(end.getTime())) return null;
  return end.getTime() < Date.now() ? 'Expired' : 'Active';
}

function hasActiveSignatureWorkflow(doc: {
  status?: string | null;
  esign_status?: string | null;
  guardian_signature_status?: string | null;
}): boolean {
  const status = String(doc.status || '').toUpperCase();
  const esign = String(doc.esign_status || '').toUpperCase();
  const guardian = String(doc.guardian_signature_status || '').toUpperCase();
  if (status === 'PENDING_SIGNATURES') return true;
  if (esign === 'SENT' || esign === 'PENDING') return true;
  if (guardian === 'SENT_FOR_SIGNATURE' || guardian === 'OVERDUE') return true;
  return false;
}

export function clinicianSigDisplay(doc: {
  doc_type?: string | null;
  title?: string | null;
  source?: string | null;
  status?: string | null;
  clinician_signature?: string | null;
  clinician_signed_at?: string | null;
  finalized_at?: string | null;
  physician_signature?: string | null;
  esign_status?: string | null;
  guardian_signature_status?: string | null;
}): string | null {
  if (doc.clinician_signature) return doc.clinician_signature;
  if (hasActiveSignatureWorkflow(doc)) return 'Pending Signature';
  if (isPhysicianSignedCpoc(doc)) return null;
  const dt = String(doc.doc_type || '').toUpperCase();
  const authoredOrFinalized = Boolean(
    doc.clinician_signed_at ||
    doc.finalized_at ||
    String(doc.status || '').toUpperCase() === 'FINALIZED' ||
    doc.source === 'MANUAL_UPLOAD' ||
    doc.source === 'INCOMING_FAX'
  );
  if (
    authoredOrFinalized &&
    (dt === 'POC' || dt === 'FEEDING_POC' || dt === 'EVAL' || dt === 'FEEDING_EVAL' || dt === 'FEEDING_EVALUATION')
  ) {
    return null;
  }
  if (dt === 'SOAP') return 'Pending Signature';
  return null;
}

export function guardianSigDisplay(doc: {
  doc_type?: string | null;
  title?: string | null;
  source?: string | null;
  status?: string | null;
  guardian_signature?: string | null;
  physician_signature?: string | null;
  esign_status?: string | null;
  guardian_signature_status?: string | null;
}): string | null {
  if (doc.guardian_signature) return doc.guardian_signature;
  if (String(doc.guardian_signature_status || '').toUpperCase() === 'WAIVED_DOCUMENTED') {
    return 'Waived / Documented';
  }
  if (hasActiveSignatureWorkflow(doc)) return 'Pending Signature';
  if (isPhysicianSignedCpoc(doc)) return null;
  const dt = String(doc.doc_type || '').toUpperCase();
  const uploadedOrFinalized = Boolean(
    String(doc.status || '').toUpperCase() === 'FINALIZED' ||
    doc.source === 'MANUAL_UPLOAD' ||
    doc.source === 'INCOMING_FAX'
  );
  if (
    uploadedOrFinalized &&
    (dt === 'POC' || dt === 'FEEDING_POC' || dt === 'EVAL' || dt === 'FEEDING_EVAL' || dt === 'FEEDING_EVALUATION' || dt === 'QPR')
  ) {
    return null;
  }
  if (dt === 'SOAP' || dt === 'INTAKE_PACKET') return 'Pending Signature';
  return null;
}
